import React, { useState, useEffect, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import usePinnedPdfWorker from '../hooks/usePinnedPdfWorker';
import { bootstrapPublicSignCapability } from '../utils/publicSignCapability';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const PublicSignPage = () => {
    const [token] = useState(() => bootstrapPublicSignCapability());
    const { ready: pdfWorkerReady, error: pdfWorkerError } = usePinnedPdfWorker();
    const [loading, setLoading] = useState(Boolean(token));
    const [loadError, setLoadError] = useState(token ? null : '署名用URLを確認できません。');
    const [contract, setContract] = useState(null);
    const [fields, setFields] = useState([]);
    const [pdfData, setPdfData] = useState(null);
    const [numPages, setNumPages] = useState(null);
    
    // 入力管理
    const [signatures, setSignatures] = useState({}); // { fieldId: "会社名" }
    const [isAgreed, setIsAgreed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);

    // 1. 契約データの取得
    const fetchContract = useCallback(async () => {
        if (!token) return;
        try {
            const publicContractUrl = `${API_URL}/api/public/contracts/${encodeURIComponent(token)}`;
            const res = await fetch(publicContractUrl, {
                cache: 'no-store',
                credentials: 'omit',
                referrerPolicy: 'no-referrer',
            });
            const data = await res.json();
            if (data.success) {
                setContract(data.contract);
                setFields(data.fields);
                setPdfData(data.pdfData);
                // GETはプレビュー/メールスキャナでも呼ばれるため、実閲覧だけを明示POSTで記録する。
                void fetch(`${API_URL}/api/public/contracts/${token}/view`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: '{}',
                    cache: 'no-store',
                    credentials: 'omit',
                    referrerPolicy: 'no-referrer',
                }).catch(() => {});
            } else {
                setLoadError(data.error || "書類が見つかりません。");
            }
        } catch (_error) {
            setLoadError('書類を取得できませんでした。');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchContract(); }, [fetchContract]);

    // 2. 署名入力処理 (枠をクリックした時)
    const handleFieldClick = (fieldId) => {
        const val = prompt("ご署名（会社名・氏名など）を入力してください");
        if (val) {
            setSignatures({ ...signatures, [fieldId]: val });
        }
    };

    // 3. 締結実行
    const handleSignSubmit = async () => {
        if (!isAgreed) return alert("規約への同意が必要です。");
        
        // 相手方の入力が全て埋まっているかチェック
        const recipientFields = fields.filter(f => f.role === 'recipient');
        const isAllFilled = recipientFields.every(f => signatures[f.id]);
        if (!isAllFilled) return alert("全ての署名箇所を入力してください。");

        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/public/contracts/${token}/sign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                cache: 'no-store',
                credentials: 'omit',
                referrerPolicy: 'no-referrer',
                body: JSON.stringify({
                    signatures: Object.entries(signatures).map(([id, val]) => ({
                        fieldId: parseInt(id),
                        value: val
                    }))
                })
            });
            const data = await res.json();
            if (data.success) {
                setIsCompleted(true);
            } else {
                alert(data.error);
            }
        } catch (err) {
            alert("通信エラーが発生しました。");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center font-bold text-slate-400">書類を準備中...</div>;

    if (loadError || pdfWorkerError) return (
        <div className="h-screen flex items-center justify-center bg-slate-50 p-6 text-center">
            <div>
                <h2 className="text-xl font-black text-slate-800">書類を表示できません</h2>
                <p className="mt-3 text-sm font-bold text-slate-500">{loadError || pdfWorkerError}</p>
            </div>
        </div>
    );
    
    if (isCompleted) return (
        <div className="h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <h2 className="text-2xl font-black text-slate-800">お手続きが完了しました</h2>
            <p className="text-slate-500 mt-4">ご確認ありがとうございました。<br />締結済みの書類は、ご登録のメールアドレスへ送付されます。</p>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-200 flex flex-col">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50 px-4 sm:px-8 py-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between shadow-sm">
                <div className="min-w-0">
                    <h1 className="break-words font-black text-slate-800">{contract?.title}</h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Document Verification</p>
                </div>
                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:items-center md:w-auto md:flex md:gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                            type="checkbox" 
                            className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={isAgreed}
                            onChange={(e) => setIsAgreed(e.target.checked)}
                        />
                        <span className="text-xs font-bold text-slate-600">契約内容に同意します</span>
                    </label>
                    <button 
                        onClick={handleSignSubmit}
                        disabled={isSubmitting || !isAgreed}
                        className="w-full bg-blue-600 text-white px-8 py-3 rounded-full font-black text-sm hover:bg-blue-700 disabled:bg-slate-300 shadow-lg shadow-blue-100 transition-all sm:w-auto"
                    >
                        {isSubmitting ? '処理中...' : '署名して完了する'}
                    </button>
                </div>
            </header>

            {/* PDF Viewer */}
            <main className="flex-grow overflow-y-auto p-3 sm:p-6 lg:p-12 flex flex-col items-center gap-6 sm:gap-8 scrollbar-hide">
                {pdfWorkerReady && <Document
                    file={`data:application/pdf;base64,${pdfData}`}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    onLoadError={() => setLoadError('書類を安全に表示できませんでした。')}
                    onSourceError={() => setLoadError('書類を安全に表示できませんでした。')}
                >
                    {Array.from(new Array(numPages), (el, index) => (
                        <div key={`page_${index + 1}`} className="relative shadow-2xl mb-10 bg-white group">
                            <Page 
                                pageNumber={index + 1} 
                                width={window.innerWidth > 900 ? 900 : window.innerWidth - 40}
                                renderTextLayer={false} // ★ 文字漏れ防止
                                renderAnnotationLayer={false} // ★ 不要な注釈レイヤーをOFF
                            />
                            
                            {/* フィールドの描画ループ */}
                            {fields.filter(f => f.page_number === index + 1).map(f => {
                                // 相手方（現在の閲覧者）の入力が必要な枠
                                if (f.role === 'recipient') {
                                    return (
                                        <div
                                            key={f.id}
                                            style={{ left: `${f.x_pos}%`, top: `${f.y_pos}%` }}
                                            onClick={() => handleFieldClick(f.id)}
                                            className={`absolute -translate-x-1/2 -translate-y-1/2 min-w-[120px] min-h-[50px] border-4 border-dashed rounded-xl flex items-center justify-center cursor-pointer transition-all hover:scale-105 ${signatures[f.id] ? 'bg-red-500/10 border-red-500' : 'bg-blue-50 border-blue-400 animate-pulse'}`}
                                        >
                                            {signatures[f.id] ? (
                                                <div className="flex flex-col items-center">
                                                    <span className="text-[8px] text-red-600 font-bold uppercase tracking-tighter">Signature</span>
                                                    <span className="text-[10px] font-black text-red-700 px-2 leading-tight">{signatures[f.id]}</span>
                                                </div>
                                            ) : (
                                                <span className="text-[10px] font-black text-blue-500">ここをクリックして署名</span>
                                            )}
                                        </div>
                                    );
                                }

                                // クライアント（自社）の配置済み署名を表示
                                if (f.role === 'sender' && f.input_value) {
                                    return (
                                        <div
                                            key={f.id}
                                            style={{ left: `${f.x_pos}%`, top: `${f.y_pos}%` }}
                                            className="absolute -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border-2 border-red-500 bg-red-50/40 flex items-center justify-center pointer-events-none"
                                        >
                                            <span className="text-[7px] font-black text-red-600 text-center px-1 break-all leading-tight">
                                                {f.input_value}
                                            </span>
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    ))}
                </Document>}
            </main>
        </div>
    );
};

export default PublicSignPage;
